import axios from 'axios';
import { cognitoAuth } from './cognitoAuth';

// Headers like 'host' cannot be set by script in the browser; the browser sets them.
const FORBIDDEN_HEADERS = ['host', 'Host'];

function headersSafeForBrowser(
  headers: Record<string, string>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (!FORBIDDEN_HEADERS.includes(key)) out[key] = value;
  }
  return out;
}

// Secure API service using Cognito authentication
export class SecureApiService {
  private baseUrl: string;
  private region: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_AGENT_API || '';
    this.region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
  }

  // Send message to Aurora with secure authentication
  async sendMessageToAurora(
    userMessage: string,
    sessionId: string,
    agent: string,
    imageUrls: string[],
    videoUrls?: string[],
    websocketId?: string
  ) {
    const useEmotion = this.getActiveAgents().includes(agent);
    const body = {
      message: userMessage,
      session_id: sessionId,
      agent: agent || 'spark',
      websocket_id: websocketId,
      image_urls: imageUrls,
      video_urls: videoUrls,
      use_emotion: useEmotion,
    };

    console.log('Sending secure message:', body);

    try {
      const url = new URL(this.baseUrl);
      const signedRequest = await cognitoAuth.createSignedRequest(
        url,
        body,
        'POST'
      );

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: headersSafeForBrowser(signedRequest.headers as Record<string, string>),
        body: signedRequest.body,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  }

  // Streaming message with secure authentication
  async sendMessageToAuroraStreaming(
    userMessage: string,
    sessionId: string,
    agent: string,
    imageUrls: string[],
    videoUrls?: string[],
    updateChat?: (partialMessage: string, isFinal: boolean) => void
  ) {
    const useEmotion = this.getActiveAgents().includes(agent);
    const body = {
      message: userMessage,
      session_id: sessionId,
      agent: agent || 'spark',
      image_urls: imageUrls,
      video_urls: videoUrls,
      use_emotion: useEmotion,
    };

    console.log('Sending secure streaming message:', body);

    try {
      const url = new URL(this.baseUrl);
      const signedRequest = await cognitoAuth.createSignedRequest(
        url,
        body,
        'POST'
      );

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: headersSafeForBrowser(signedRequest.headers as Record<string, string>),
        body: signedRequest.body,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        console.error('No response body received');
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        // Pass only the delta chunk so callers can control accumulation
        if (updateChat) updateChat(chunk, false);
      }

      // Signal stream end; callers can finalize with their own buffer
      if (updateChat) updateChat('', true);
    } catch (error) {
      console.error('Failed to send streaming message:', error);
      throw error;
    }
  }

  // Get configuration with secure authentication
  async getConfig(agent: string) {
    const body = {
      agent: agent || 'spark',
      config: true,
    };

    try {
      const url = new URL(this.baseUrl);
      const signedRequest = await cognitoAuth.createSignedRequest(
        url,
        body,
        'POST'
      );

      return axios.post(this.baseUrl, signedRequest.body, {
        headers: headersSafeForBrowser(signedRequest.headers as Record<string, string>),
      });
    } catch (error) {
      console.error('Failed to get config:', error);
      throw error;
    }
  }

  // Get itinerary with secure authentication
  async getItineraryFromSessionId(sessionId: string) {
    const url = process.env.REACT_APP_FETCH_ITINERARY_API || '';
    const body = {
      session_id: sessionId,
    };

    try {
      const urlObj = new URL(url);
      const signedRequest = await cognitoAuth.createSignedRequest(
        urlObj,
        body,
        'GET'
      );

      return axios.get(url, {
        headers: headersSafeForBrowser(signedRequest.headers as Record<string, string>),
      });
    } catch (error) {
      console.error('Failed to get itinerary:', error);
      throw error;
    }
  }

  // Get active agents list
  private getActiveAgents(): string[] {
    return [
      'spark',
      'robin',
      'felix',
      'houston',
      'atlanta',
      'chicago',
      'new_york',
    ];
  }

  // Check authentication status
  async isAuthenticated(): Promise<boolean> {
    return await cognitoAuth.isAuthenticated();
  }

  // Logout
  logout() {
    cognitoAuth.clearCredentials();
  }
}

// Singleton instance
export const secureApi = new SecureApiService();
